'use client';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { HandCoins, QRRound, ShoppingCart, ID, Settings, Shield, Bell, Rocket } from '@/public/images/icons/icons';
import { fadeInUp, viewportOnce } from '@/lib/animations';

export default function WhatAbout() {
  const t = useTranslations('Banks.SharedNetwork');

  const architectureLayers = [
    {
      badgeKey: 'arch_layer_channel_badge',
      tags: ['Bank A app', 'Bank B app', 'Bank C app', 'Bank D app', '+ more banks'],
      isHighlightLast: true,
    },
    {
      badgeKey: 'arch_layer_overlay_badge',
      tags: [
        'Bank ID directory',
        'Shared brand',
        'FCL Rules Engine',
        'EMPSA roaming',
        'P2P · QR · E-com rulebooks',
      ],
    },
    {
      badgeKey: 'arch_layer_settlement_badge',
      tags: ['AIPS (today)', 'TIPS Clone (Nov 2026)', 'SEPA instant'],
    },
  ];

  const benefitsCards = [
    {
      id: 'p2p',
      titleKey: 'benefit_p2p_title',
      descKey: 'benefit_p2p_desc',
      icon: <HandCoins width={30} height={30} className="shrink-0"/>
    },
    {
      id: 'p2b',
      titleKey: 'benefit_p2b_title',
      descKey: 'benefit_p2b_desc',
      icon: <QRRound width={30} height={30} className="shrink-0" fill="none"/>
    },
    {
      id: 'ecommerce',
      titleKey: 'benefit_ecom_title',
      descKey: 'benefit_ecom_desc',
      icon: <ShoppingCart width={30} height={30} className="shrink-0"/>
    },
    {
      id: 'directory',
      titleKey: 'benefit_directory_title',
      descKey: 'benefit_directory_desc',
      icon: <ID width={30} height={30} className="shrink-0"/>
    },
    {
      id: 'fcl',
      titleKey: 'benefit_fcl_title',
      descKey: 'benefit_fcl_desc',
      icon: <Settings width={30} height={30} className="shrink-0"/>
    },
    {
      id: 'security',
      titleKey: 'benefit_security_title',
      descKey: 'benefit_security_desc',
      icon: <Shield width={30} height={30} className="shrink-0"/>
    },
    {
      id: 'notifications',
      titleKey: 'benefit_notifications_title',
      descKey: 'benefit_notifications_desc',
      icon: <Bell width={30} height={30} className="shrink-0"/>
    },
    {
      id: 'white_label',
      titleKey: 'benefit_white_label_title',
      descKey: 'benefit_white_label_desc',
      icon: <Rocket width={30} height={30} className="shrink-0"/>
    },
  ];

  return (
    <motion.section
      aria-labelledby="shared-network-heading"
      className="w-full bg-very-light py-16 sm:py-24 px-6 sm:px-10 lg:px-16"
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      variants={fadeInUp}
    >
      <div className="max-w-8xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-6 space-y-10">
          <div className="space-y-4">
            <span className="text-[10px] sm:text-xs font-bold tracking-widest color-primary uppercase block">
              {t('left_badge')}
            </span>
            <h2 id="shared-network-heading" className="text-3xl sm:text-5xl font-extrabold text-gray-900 leading-[1.15] tracking-tight">
              {t.rich('left_heading', {
                highlight: (chunks) => <span className="color-primary">{chunks}</span>,
              })}
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 leading-relaxed font-normal pt-2">
              {t('left_subheading')}
            </p>
          </div>
          <div className="space-y-6 pt-2">
            {architectureLayers.map((layer, idx) => (
              <div key={idx} className="space-y-2">
                <span className="text-[10px] font-bold color-primary uppercase tracking-wider block">
                  {t(layer.badgeKey)}
                </span>
                <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm flex flex-wrap gap-2 items-center">
                  {layer.tags.map((tag, tagIdx) => {
                    const isLast = tagIdx === layer.tags.length - 1;
                    const isMuted = layer.isHighlightLast && isLast;
                    return (
                      <span
                        key={tagIdx}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                          isMuted
                            ? 'bg-slate-50 border-slate-200 text-slate-400 font-normal'
                            : 'bg-blue-50/70 border-blue-100 text-blue-700'
                        }`}
                      >
                        {tag}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6 space-y-6">
          <span className="text-[10px] sm:text-xs font-bold tracking-widest color-primary uppercase block">
            {t('right_badge')}
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {benefitsCards.map((card) => (
              <div
                key={card.id}
                className="bg-white flex flex-col md:flex-row gap-3 rounded-2xl p-6 border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col justify-start space-y-4"
              >
                <div className="w-10 h-10 flex items-center justify-center shrink-0">
                  {card.icon}
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 tracking-tight">
                    {t(card.titleKey)}
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed font-normal">
                    {t(card.descKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
}